import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Form,
  notification,
  DatePicker,
  Input,
  Typography,
  Button,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import axiosInstance from "utilities/axios";

const { Text } = Typography;
function ScheduleCalibration({
  isVisible,
  onClose,
  clinicNo,
  scheduledCalibrations,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lastCalibration, setLastCalibration] = useState(null);
  const [nextCalibration, setNextCalibration] = useState(null);

  useMemo(() => {
    if (scheduledCalibrations.length === 0) {
      return null;
    }

    const today = dayjs().startOf("day"); // Start of today (00:00)

    // Return the most recent calibration before today
    var localLastCalibration = scheduledCalibrations
      .filter((calibration) =>
        dayjs(calibration.scheduledDate).isBefore(today, "day")
      )
      .sort((a, b) => dayjs(b.scheduledDate).diff(dayjs(a.scheduledDate)))[0];
    setLastCalibration(localLastCalibration);

    // Return the next calibration after today
    var localNextCalibration = scheduledCalibrations
      .filter((calibration) =>
        dayjs(calibration.scheduledDate).isAfter(today, "day")
      )
      .sort((a, b) => dayjs(a.scheduledDate).diff(dayjs(b.scheduledDate)))[0];
    setNextCalibration(localNextCalibration);
  }, [scheduledCalibrations]);

  useEffect(() => {
    if (isVisible) {
      form.resetFields();
      form.setFieldsValue({
        scheduledDate: nextCalibration
          ? dayjs(nextCalibration.scheduledDate)
          : null,
        comments: nextCalibration ? nextCalibration.visitNotes : "",
      });
    }
  }, [isVisible, nextCalibration, form]);

  const handleCancel = () => {
    // include content type in the header
    axiosInstance
      .delete(`equipment/calibrations/schedule/${nextCalibration.visitID}`)
      .then(() => {
        notification.success({
          message: "Success",
          description: "Calibration visit cancelled successfully",
        });
        onClose();
      })

      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };
  const handleSubmit = (values) => {
    setLoading(true);

    var payload = {
      clinicNo: clinicNo,
      scheduledDate: values.scheduledDate.format("YYYY-MM-DD"),
      notes: values.comments,
    };
    const handleResponse = (promise) => {
      promise
        .then(() => {
          notification.success({
            message: "Success",
            description: "Calibration scheduled successfully",
          });
          onClose();
        })
        .catch((error) => {
          notification.error({
            message: "Error",
            description: error.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (nextCalibration) {
      handleResponse(
        axiosInstance.patch(
          `equipment/calibrations/schedule/${nextCalibration.visitID}`,
          payload
        )
      );
    } else {
      handleResponse(
        axiosInstance.post("equipment/calibrations/schedule", payload)
      );
    }
  };

  return (
    <Modal
      title="Schedule Calibration"
      open={isVisible}
      onCancel={onClose}
      footer={
        <>
          {nextCalibration && (
            <Popconfirm
              title="Are you sure you would like to cancel this visit?"
              onConfirm={handleCancel}
            >
              <Button type="primary" danger>
                Cancel
              </Button>
            </Popconfirm>
          )}

          <Button type="primary" onClick={form.submit} loading={loading}>
            {nextCalibration ? "Reschedule" : "Schedule"}
          </Button>
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          scheduledDate: dayjs(nextCalibration?.scheduledDate),
          comments: nextCalibration?.visitNotes,
        }}
      >
        <Form.Item
          name="scheduledDate"
          label="Scheduled Date"
          rules={[
            { required: true, message: "Please select a date" },
            // rule to check if the date is in the future
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (dayjs(value).isAfter(dayjs().startOf("day"))) {
                  return Promise.resolve();
                }
                return Promise.reject("Please select a future date");
              },
            }),
          ]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        {lastCalibration && (
          <Text type="secondary">
            Last Calibration Visit:{" "}
            {dayjs(lastCalibration.scheduledDate).format("YYYY-MM-DD")}
          </Text>
        )}

        <Form.Item name="comments" label="Comments" style={{ marginTop: 10 }}>
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ScheduleCalibration;
